<script lang="ts" setup>
import { computed, onMounted, ref, type PropType } from 'vue';

import type { MarketModel } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';

import MarketsContainer from '../MarketsContainer.vue';
import useMarketListFilters from '../useMarketListFilters';

const props = defineProps({
  isFutures: { type: Boolean, default: false },
  market: { type: Object as PropType<MarketModel>, required: false },
  marketsList: { type: Array as PropType<Array<MarketModel>>, required: false },
  showMarketList: { type: Boolean, default: false },
});

const showML = ref(false);
const toggleSelect = () => {
  showML.value = !showML.value;
};

const emit = defineEmits<{
  (e: 'market-clicked', value: string): void;
}>();

const { applyMarketFilters } = useMarketListFilters();

const dropdownMaxHeight = ref(496);

const marketName = computed(() => props.market?.name);
const marketId = computed(() => props.market?.id);
const marketBaseCode = computed(() => props.market?.base.code);

const onSearchCallback = (search?: string) => applyMarketFilters({ search });

const onMarketSelect = (market: MarketModel) => {
  emit('market-clicked', market.id);
};
const onMarketListSelect = (mId: string) => {
  emit('market-clicked', mId);
  toggleSelect();
};

onMounted(() => {
  const appHeight = document.querySelector('#app')?.scrollHeight;

  if (appHeight) {
    dropdownMaxHeight.value = appHeight / 2;
  }
});
</script>

<template>
  <div
    v-if="showMarketList"
    :class="['market-header-market-select market-header-market-select--list', { 'market-header-market-select--opened': showML }]"
  >
    <div
      class="market-header-market-select__activator"
      @click="toggleSelect"
    >
      <div class="market-header-market-select__activator-market-data">
        <x-logo
          v-if="marketBaseCode"
          :key="marketName"
          class="market-header-market-select__market-logo"
          :name="marketBaseCode"
          type="crypto"
        />
        <div class="market-header-market-select__market-name">
          {{ marketName }}
        </div>
        <ot-lozenge
          v-if="market?.type === MarketType.PERP"
          :uppercase="false"
          :filled="false"
          :text="$t('fundamentals.perpetual.short')"
          class="market-header-market-select__lozenge"
        />
      </div>
      <div class="market-header-market-select__activator-arrow">
        <ot-icon
          name="arrow-down"
          :class="{ 'market-header-market-select__activator-arrow--reversed': showML }"
        />
      </div>
    </div>
    <div
      v-if="showML"
      class="market-header-market-select__content"
    >
      <markets-container
        v-if="marketId"
        variant="dark-medium"
        :market-id="marketId"
        :with-logo="true"
        :search-in-focus="true"
        :is-futures="isFutures"
        @market-clicked="onMarketListSelect"
      />
    </div>
  </div>
  <ot-select
    v-if="!showMarketList && market && marketsList"
    ref="marketSelect"
    class="market-header-market-select"
    label="name"
    variant="dark-borderless"
    :dropdown-max-height="`${dropdownMaxHeight}px`"
    :on-search-cb="onSearchCallback"
    :options="marketsList"
    :searchable="true"
    :value="market"
    @update:value="onMarketSelect"
  >
    <template #selected-option>
      <div class="market-header-market-select__market-info">
        <x-logo
          v-if="marketBaseCode"
          :key="marketName"
          class="market-header-market-select__market-logo"
          :name="marketBaseCode"
          type="crypto"
        />
        <div class="market-header-market-select__market-name">
          {{ marketName }}
        </div>
        <ot-lozenge
          v-if="isFutures"
          :uppercase="false"
          :filled="false"
          :text="$t('fundamentals.perpetual.short')"
          class="market-header-market-select__lozenge"
        />
      </div>
    </template>
    <template #option="{ name, id, base }">
      <x-list-item
        class="market-header-market-select__option-item"
        :active="market.id === id"
        :title="name"
        :left-icon-name="base.code"
        left-icon-variant="logo"
      >
        <template
          v-if="isFutures"
          #label-additional-content
        >
          <ot-lozenge
            :uppercase="false"
            :filled="false"
            :text="$t('fundamentals.perpetual.short')"
            class="market-header-market-select__lozenge"
          />
        </template>
      </x-list-item>
    </template>
  </ot-select>
</template>

<style lang="scss">
.market-header-market-select {
  padding: 8px 12px;

  &__market-info {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  &__market-logo {
    width: 26px;
    aspect-ratio: 1;
  }

  &__market-name {
    font-size: var(--font-size-lg);
  }

  &__activator {
    display: flex;
    max-width: 180px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    gap: 16px;
  }

  &__activator-arrow {
    display: flex;
    width: 16px;
    align-items: center;
    color: rgb(var(--v-theme-text-secondary));

    &--reversed {
      transform: rotate(180deg);
    }
  }

  &__activator-market-data {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  &__content {
    position: absolute;
    z-index: var(--header-dropdown-menu-z-index);
    top: calc(100% + 6px);
    left: 0;
    width: 430px;
  }
}

.market-header-market-select--list {
  position: relative;
}

.market-header-market-select--list:hover,
.market-header-market-select--opened {
  border-radius: var(--app-border-radius);
}
</style>
